import {
  arrayUnion,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  writeBatch,
} from 'firebase/firestore';
import { statusLabel } from '../constants/tickets';

/**
 * Create a support ticket linked to an agile task (batch write).
 * @param {import('firebase/firestore').Firestore} db
 * @param {object} opts
 * @param {object} opts.task — task doc with id, title, description, companyId
 * @param {string} opts.companyId
 * @param {string} opts.body — ticket description
 * @param {string} opts.uid
 * @param {string} [opts.email]
 * @returns {Promise<{ ticketId: string }>}
 */
export async function createLinkedEscalationTicket(db, {
  task,
  companyId,
  body,
  uid,
  email = '',
}) {
  if (!task?.id || !companyId || !uid) {
    throw new Error('Missing task, company, or user.');
  }

  const ticketRef = doc(collection(db, 'tickets'));
  const taskRef = doc(db, 'tasks', task.id);
  const title = `[Task Escalation] ${task.title || 'Untitled task'}`.slice(0, 500);
  const assignedUsers = [...new Set([uid, task.assigneeUid].filter(Boolean))];

  const batch = writeBatch(db);
  batch.set(ticketRef, {
    companyId,
    title,
    body: (body || task.description || '').trim(),
    status: 'open',
    priority: 'medium',
    labelIds: [],
    assigneeUid: task.assigneeUid || null,
    assignedUsers,
    platformEscalation: false,
    archived: false,
    archivedAt: null,
    reporterUid: uid,
    linkedTaskId: task.id,
    source: 'task_escalation',
    aiEnriched: false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdBy: uid,
    updatedBy: uid,
  });
  batch.update(taskRef, {
    linkedTicketIds: arrayUnion(ticketRef.id),
    updatedAt: serverTimestamp(),
    updatedBy: uid,
  });
  await batch.commit();

  return { ticketId: ticketRef.id, title, actorEmail: email };
}

/**
 * @param {import('firebase/firestore').Firestore} db
 * @param {string[]} ticketIds
 * @param {string} [companyId] required for tenant isolation (drops cross-tenant docs)
 * @returns {Promise<object[]>}
 */
export async function fetchLinkedTicketsByIds(db, ticketIds, companyId) {
  const ids = [...new Set((ticketIds || []).filter(Boolean))];
  const tenantId = typeof companyId === 'string' ? companyId.trim() : '';
  if (!ids.length) return [];

  const rows = await Promise.all(
    ids.map(async (id) => {
      try {
        const snap = await getDoc(doc(db, 'tickets', id));
        if (!snap.exists()) return null;
        const row = { id: snap.id, ...snap.data() };
        if (tenantId && row.companyId !== tenantId) return null;
        return row;
      } catch (e) {
        console.warn('fetchLinkedTicketsByIds:', id, e);
        return null;
      }
    }),
  );

  return rows.filter(Boolean);
}

/**
 * @param {object} ticket
 * @returns {string}
 */
export function linkedTicketStatusLabel(ticket) {
  return statusLabel(ticket?.status);
}

/**
 * @param {import('firebase/firestore').Firestore} db
 * @param {string} taskId
 * @returns {Promise<object|null>}
 */
export async function fetchLinkedTaskSummary(db, taskId) {
  if (!taskId) return null;
  const snap = await getDoc(doc(db, 'tasks', taskId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}
